// Fetch wrapper (cookie-less): sends the session token as `Authorization: Bearer`, normalizes the
// error envelope into ApiError, and clears the stored token on 401.
import { env } from "../env.js";
import { clearToken, getToken } from "../auth/token.js";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown; requestId?: string };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const isForm = body instanceof FormData;
  const token = getToken();
  const headers: Record<string, string> = {};
  if (body && !isForm) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${env.apiBase}${path}`, {
    method,
    credentials: "omit", // cookie-less: auth rides in the Authorization header
    headers,
    body: body == null ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  });

  if (res.status === 401) clearToken(); // stale/invalid token -> drop it so the app re-prompts

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const env2 = (data ?? {}) as ErrorEnvelope;
    throw new ApiError(
      res.status,
      env2.error?.code ?? "ERROR",
      env2.error?.message ?? res.statusText,
      env2.error?.details,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
  /** Absolute URL builder for redirects (login) and same-origin links. */
  url: (path: string) => `${env.apiBase}${path}`,
};
